import React from 'react';
import { useParams } from 'react-router-dom';

import BaseSettingCard from '@dashboard/components/platform-settings/BaseSettingCard';
import {
  usePlatformQuery,
  useUpdatePlatformConfigurationMutation,
  WaypointFeatureTier,
} from '@dashboard/generated/wp/authentication';
import { TipsAndUpdatesOutlined } from '@mui/icons-material';
import { Alert, FormControl, FormControlLabel, FormGroup, Link, Stack, Switch, Typography } from '@mui/material';
import { alertError, alertSuccess } from '@wp-react/notifications';

type TPayload = {
  enableErrorNotificationEmails: boolean;
};

export default function AlertsCard() {
  const { platformId } = useParams<{ platformId: string }>();
  const { data } = usePlatformQuery({ variables: { id: platformId! } });

  const platform = data?.Platform;

  const [executeMutation, { loading }] = useUpdatePlatformConfigurationMutation({
    refetchQueries: ['Platform'],
  });

  const onSubmit = async ({ enableErrorNotificationEmails }: TPayload) => {
    if (platform === undefined) {
      return;
    }
    try {
      await executeMutation({
        variables: {
          platformId: platform.id,
          enableErrorNotificationEmails,
        },
      });
      alertSuccess('Event tracking updated');
    } catch {
      alertError('Failed to update event tracking');
    }
  };

  const isDisabled = () => {
    if (loading) {
      return true;
    }
    if (platform === undefined) {
      return true;
    }
    switch (platform.activeWaypointTier) {
      case WaypointFeatureTier.Unlimited:
      case WaypointFeatureTier.Growth:
      case WaypointFeatureTier.Pro:
        return false;
      case WaypointFeatureTier.Free:
      case WaypointFeatureTier.Sample:
      case WaypointFeatureTier.Sandbox:
      case WaypointFeatureTier.Starter:
        return true;
    }
  };

  let descriptionColor: string | undefined = undefined;
  if (!isDisabled()) {
    descriptionColor = 'text.secondary';
  }

  const isLimited = () => {
    if (!platform) {
      return false;
    }
    if (platform.enableErrorNotificationEmails) {
      return false;
    }
    switch (platform.activeWaypointTier) {
      case WaypointFeatureTier.Growth:
      case WaypointFeatureTier.Pro:
      case WaypointFeatureTier.Unlimited:
        return false;
      case WaypointFeatureTier.Free:
      case WaypointFeatureTier.Sample:
      case WaypointFeatureTier.Sandbox:
      case WaypointFeatureTier.Starter:
        return true;
    }
  };

  let upgradeAlert: JSX.Element | null = null;
  if (isLimited()) {
    upgradeAlert = (
      <Alert variant="outlined" severity="info" icon={<TipsAndUpdatesOutlined />}>
        <Typography>
          <Link href="https://www.usewaypoint.com/docs/manage-plan/" fontWeight="medium" target="_blank">
            Upgrade to alert your team
          </Link>{' '}
          when actionable events happen within your workspace.
        </Typography>
      </Alert>
    );
  }

  return (
    <BaseSettingCard title="Alerts">
      <FormControl component="fieldset" variant="standard" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormGroup>
          <FormControlLabel
            label={
              <Stack spacing={0.5}>
                <Typography>Failed messages</Typography>
                <Typography variant="body2" color={descriptionColor}>
                  Notify your team when an email fails before it is sent. Errors are summarized and delivered hourly.
                </Typography>
              </Stack>
            }
            labelPlacement="end"
            sx={{ gap: 1, alignItems: 'flex-start' }}
            control={
              <Switch
                disabled={isDisabled()}
                checked={platform?.enableErrorNotificationEmails}
                size="small"
                onChange={(ev, checked: boolean) => {
                  onSubmit({ enableErrorNotificationEmails: checked });
                }}
              />
            }
          />
        </FormGroup>
        {upgradeAlert}
      </FormControl>
    </BaseSettingCard>
  );
}
